import React, { useState } from 'react';
import Button from '@material-ui/core/Button';
import CssBaseline from '@material-ui/core/CssBaseline';
import Grid from '@material-ui/core/Grid';
import Container from '@material-ui/core/Container';
import GroupRenderer from '../Fields/GroupFields/GroupRenderer';
import { reduxForm, InjectedFormProps } from 'redux-form';
import LegalInfoField, {
	LegalInfoFieldUi,
} from '../Fields/FormFields/Components/LegalInfo/LegalInfo';
import { FieldUi } from '../Fields/FormFields/FieldUi';
import { Field, TermsAndConditions } from '../../api/applicationDataApi';
import { DynamicFormStyle, SpinnerButton } from '@danfoss/webex-ui/dist/mui';
import { useTranslation } from 'react-i18next';
import { getTranslationUi } from '../Fields/TranslationUi';
import settings from '../../config/settings';
import { Loading } from '../../store/loading/types';
import classnames from 'classnames';
import { validate, warn } from './utils';
import { useStyles } from './styles';

const formId = 'DynamicForm';

export interface FormInitialValues {
	termAndConditions: TermsAndConditions;
	privacyPolicy: TermsAndConditions;
	legalInfo: {
		privacyPolicyData: LegalInfoFieldUi;
		danfossDipPrivacyPolicyData: LegalInfoFieldUi;
		termsAndConditionsData: LegalInfoFieldUi;
		danfossDipTermsAndConditionsData: LegalInfoFieldUi;
		piplTermsData: LegalInfoFieldUi;
	};
	country: string; // as default value
	applicationFields: Field[];
	requireLinkClick: boolean
}

interface AdditionalProps {
	formValues: any;
	userRelatedFields: FieldUi[];
	loading: Loading;
}

interface LegalInfoChecked {
	danfossDipTermsAndConditions: boolean | undefined;
	termsAndConditions: boolean | undefined;
	danfossDipPrivacyPolicy: boolean | undefined;
	privacyPolicy: boolean | undefined;
	piplTerms: boolean | undefined;
}

const DynamicForm: React.FC<InjectedFormProps<FormInitialValues> &
	AdditionalProps> = props => {
	const {
		handleSubmit,
		initialValues,
		pristine,
		formValues,
		userRelatedFields,
		loading,
		valid
	} = props;
	const [danfossDipTermsAndConditionLinkClicked, setDanfossDipTermsAndConditionLinkClicked] = useState(false);
	const [termsAndConditionLinkClicked, setTermsAndConditionLinkClicked] = useState(false);
	const [privacyPolicyLinkClicked, setPrivacyPolicyLinkClicked] = useState(false);
	const [appSpecificPrivacyPolicyLinkClicked, setAppSpecificPrivacyPolicyLinkClicked] = useState(false);
	const [piplTermsLinkClicked, setPiplTermsLinkClicked] = useState(false);
	const [danfossDipTermsAndConditionErrorText, setDanfossDipTermsAndConditionErrorText] = useState("");
	const [termsAndConditionErrorText, setTermsAndConditionErrorText] = useState("");
	const [privacyPolicyErrorText, setPrivacyPolicyErrorText] = useState("");
	const [piplTermsErrorText, setPiplTermsErrorText] = useState("");

	const { legalInfo, applicationFields, requireLinkClick  } = initialValues;
	const filledFields: string[] | undefined = formValues && Object.keys(formValues).filter(
		(key: string) => {
			// Special check for multiselect dropdowns where formValues contains an entry with empty value if none of the options are selected
			// In this case the submit button should be disabled
			if (formValues[key] && Array.isArray(formValues[key])) {
				return formValues[key].some((value: any) => !!value);
			}
			return true
		});

	const userRelatedFieldsUpdated = userRelatedFields.map(relField => {

		const appField = applicationFields?.find(field => field.name === relField.name);

		return {...relField, required: !appField?.is_optional || false}

	})

	const isTermsAndConditionNeeded = legalInfo!.termsAndConditionsData.termsAndConditionIsNeeded;
	const isDanfossDipPrivacyPolicyNeeded = legalInfo!.danfossDipPrivacyPolicyData.danfossDipPrivacyPolicyIsNeeded;
	const isPrivacyPolicyNeeded = legalInfo!.privacyPolicyData.privacyPolicyIsNeeded;
	const isDanfossDipTermsAndConditionNeeded = legalInfo!.danfossDipTermsAndConditionsData.danfossDipTermsAndConditionIsNeeded;
	const isPiplTermsNeeded = legalInfo && legalInfo.piplTermsData && legalInfo.piplTermsData.piplTermsIsNeeded;

	const isSubmitable = (
		userRelFields: FieldUi<string | boolean>[],
		legalInfoChecked: LegalInfoChecked,
		fieldsFilled: string[] = []
	) => {

		if (
			!legalInfoChecked ||
			(isTermsAndConditionNeeded && !legalInfoChecked.termsAndConditions)
		) {
			return false;
		}
		if (
			!legalInfoChecked ||
			(isPrivacyPolicyNeeded && !legalInfoChecked.privacyPolicy)
		) {
			return false;
		}
		if (
			!legalInfoChecked ||
			(isDanfossDipPrivacyPolicyNeeded && !legalInfoChecked.danfossDipPrivacyPolicy)
		) {
			return false;
		}
		if (
			!legalInfoChecked ||
			(isDanfossDipTermsAndConditionNeeded &&
				!legalInfoChecked.danfossDipTermsAndConditions)
		) {
			return false;
		}
		if (
			!legalInfoChecked ||
			(isPiplTermsNeeded &&
				!legalInfoChecked.piplTerms)
		) {
			return false;
		}

		for (const field of userRelFields) {

			const appField = (applicationFields as Field[]).find(afield => afield.name === field.name);

			if (!fieldsFilled.includes(field.name) && field.required && !(appField && appField.is_optional)) {
				return false;
			}
		}
		if (
			Object.keys(validate(fieldsFilled, { userRelFields })).length !== 0
		) {
			return false;
		}

		return true;
	};

	const classes = DynamicFormStyle();
	const localClasses = useStyles();
	const { t } = useTranslation();
	const cancel = () => {
		window.history.go(-1);
	};

	const LegalInfo: LegalInfoChecked = {
		danfossDipTermsAndConditions: formValues && formValues.danfoss_dip_terms_and_conditions,
		termsAndConditions: formValues?.terms_and_conditions,
		danfossDipPrivacyPolicy: formValues?.danfoss_dip_privacy_policy,
		privacyPolicy: formValues?.privacy_policy,
		piplTerms: formValues?.pipl_terms,
	};

	return (
		<Container>
			<CssBaseline />
			<div>
				{((userRelatedFields && userRelatedFields.length) ||
					isTermsAndConditionNeeded ||
					isDanfossDipPrivacyPolicyNeeded ||
					isPrivacyPolicyNeeded ||
					isDanfossDipTermsAndConditionNeeded ||
					isPiplTermsNeeded) && (
					<form
						className={classnames(classes.form, localClasses.form)}
						id="DynamicForm"
						onSubmit={handleSubmit}
					>
						<Grid container spacing={2} className={localClasses.groupContainer}>
							<GroupRenderer
								loading={loading.formLoading}
								fields={userRelatedFieldsUpdated || []}
							/>
							{isDanfossDipTermsAndConditionNeeded
								? LegalInfoField({
										...legalInfo!.danfossDipTermsAndConditionsData,
										loading: loading.formLoading,
										linkClicked: danfossDipTermsAndConditionLinkClicked,
										setLinkClicked: () => {setDanfossDipTermsAndConditionErrorText(''); setDanfossDipTermsAndConditionLinkClicked(true)},
										setErrorText: () => setDanfossDipTermsAndConditionErrorText(getTranslationUi("validation_messages.link_click_required") as string),
										errorText: danfossDipTermsAndConditionErrorText,
										requireLinkClick
								  })
								: null}
							{isTermsAndConditionNeeded
								? LegalInfoField({
										...legalInfo!.termsAndConditionsData,
										loading: loading.formLoading,
										linkClicked: termsAndConditionLinkClicked,
										setLinkClicked: () => {setTermsAndConditionErrorText(''); setTermsAndConditionLinkClicked(true)},
										setErrorText: () => setTermsAndConditionErrorText(getTranslationUi("validation_messages.link_click_required") as string),
										errorText: termsAndConditionErrorText,
										requireLinkClick
								  })
								: null}
							{isDanfossDipPrivacyPolicyNeeded
								? LegalInfoField({
										...legalInfo!.danfossDipPrivacyPolicyData,
										loading: loading.formLoading,
										linkClicked: privacyPolicyLinkClicked,
										setLinkClicked: () => {setPrivacyPolicyErrorText('');setPrivacyPolicyLinkClicked(true)},
										setErrorText: () => setPrivacyPolicyErrorText(getTranslationUi("validation_messages.link_click_required") as string),
										errorText: privacyPolicyErrorText,
										requireLinkClick
								  })
								: null}
							{isPrivacyPolicyNeeded
								? LegalInfoField({
										...legalInfo!.privacyPolicyData,
										loading: loading.formLoading,
										linkClicked: appSpecificPrivacyPolicyLinkClicked,
										setLinkClicked: () => {setPrivacyPolicyErrorText('');setAppSpecificPrivacyPolicyLinkClicked(true)},
										setErrorText: () => setPrivacyPolicyErrorText(getTranslationUi("validation_messages.link_click_required") as string),
										errorText: privacyPolicyErrorText,
										requireLinkClick
								  })
								: null}
							{isPiplTermsNeeded
								? LegalInfoField({
										...legalInfo.piplTermsData,
										loading: loading.formLoading,
										linkClicked: piplTermsLinkClicked,
										setLinkClicked: () => {setPiplTermsErrorText(''); setPiplTermsLinkClicked(true)},
										setErrorText: () => setPiplTermsErrorText(getTranslationUi("validation_messages.link_click_required") as string),
										errorText: piplTermsErrorText,
										requireLinkClick
								  })
								: null}
							{loading.loadingError && loading.loadingError.message ? (
								<Grid container item className={localClasses.errorMessage}>
									{loading.loadingError.message}
								</Grid>
							) : null}
						</Grid>
						<div className={localClasses.buttonsSection}>
							<SpinnerButton
								type="submit"
								variant="contained"
								color="primary"
								className={`text-transform`}
								disabled={
									!isSubmitable(
										userRelatedFields || [],
										formValues && LegalInfo,
										filledFields
									) || pristine || !valid
								}
								pathToImagesFolder={`${settings.staticFiles.endpoint}/images`}
								spinnerVisible={loading.formLoading}
							>
								{loading.formLoading
									? t('button.loading')
									: t('button.submit')}
							</SpinnerButton>
							<Button
								type="button"
								variant="outlined"
								color="default"
								onClick={cancel}
								className={`text-transform ${localClasses.buttonMargin}`}
							>
								{t('button.cancel')}
							</Button>
						</div>
					</form>
				)}
			</div>
		</Container>
	);
};

export default reduxForm({
	validate,
	warn,
	form: formId, // a unique identifier for this form
	touchOnChange: true,
	enableReinitialize: true,
	// asyncValidate
})(DynamicForm);
